import { unwrapResult } from '@reduxjs/toolkit';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMostRecentLocations } from 'emergency_data/src/utils/getMostRecentLoc';
import { Field, Formik, Form, FieldArray } from 'formik';
import PropTypes from 'prop-types';
import ChatAvatar from '@rsos/components/capstone/Chat/ChatAvatar';
import Loader from '@rsos/components/capstone/Loader';
import {
  Box,
  FakeLinkComponent,
  Text,
  TextArea,
  Tooltip,
} from '@rsos/components/capstone/base';
import {
  DropdownDialog,
  DropdownBody,
} from '@rsos/components/capstone/base/Dropdown/DropdownComponents';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import {
  setSharedWithAgencies,
  shareEmergencyCall,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { fetchAgencyLinks } from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgenciesSlice';
import { notifyError } from '@rsos/utils/toastUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import { trackShareButtonClick } from '../agencyShareChatTrackings';
import { toastShared } from '../utils/agencyShareChatNotificationHelpers';
import {
  Title,
  Subtitle,
  Label,
  Checkbox,
  ShareButton,
  DropdownHeader,
  DropdownFooter,
  DropdownList,
  DropdownListItem,
} from './AgencyShareChatDropdown.styles';

const API_ERROR_MSG = 'We encountered an error while sharing this call.';

/**
 * share incident dropdown that is displayed when user
 * clicks on the share button in the calls event pane
 *
 * @param {function} onShare - toggles the visibility of the share dropdown
 * @param {array} peerAgencies - list of linked peer agencies
 */
const AgencyShareChatDropdown = ({ onShare, peerAgencies }) => {
  const dispatch = useDispatch();
  const { layout } = useContext(LayoutContext);
  const { irpVersion } = useIsIRP();
  const { name: orgName } = useSelector(state => state.sinatra.user.currentOrg);
  const { currentPSAP } = useSelector(state => state.psaps);
  const isLoading = useSelector(
    state => state.emergencyCalls.loading?.shareEmergencyCall,
  );
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const sharedWithAgencies = useSelector(
    state =>
      state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
        ?.sharedWithAgencies || [],
  );
  const currentCall = useSelector(
    state => state.emergencyCalls.callQueue[selectedCallerID] || {},
  );

  const { mostRecentLocation } = getMostRecentLocations(currentCall);

  let currentCallLocation = { ...mostRecentLocation };
  const initialValues = {
    peerAgencies: sharedWithAgencies,
  };
  const [agencyDisplayNameList, setAgencyDisplayNameList] = useState([]);
  const [tooltipID, setTooltipID] = useState(null);

  const numActiveAgencies = useMemo(() => {
    const sum = peerAgencies.reduce((count, agency) => {
      if (agency.link_to.active) count += 1;
      return count;
    }, 0);

    return sum;
  }, [peerAgencies]);

  /**
   * get list of event chat participants for the ChatHeader component
   * @param {array} agencyAccountIDs - list of agency account IDs in strings
   * @returns array of chat participant objects
   */
  const getEventChatParticipants = agencyAccountIDs => {
    const currentAgency = {
      account_id: currentPSAP.account_id,
      display_name: currentPSAP.display_name,
      contact_phone: currentPSAP?.contact_phone || '',
    };
    const sharedAgencies = [];
    sharedAgencies.push(currentAgency);

    agencyAccountIDs.forEach(accountID => {
      peerAgencies.forEach(agency => {
        if (agency.link_to.account_id === accountID) {
          const agencyData = {
            account_id: agency.link_to.account_id,
            display_name: agency.link_to.display_name,
          };
          sharedAgencies.push(agencyData);
        }
      });
    });

    return sharedAgencies;
  };

  /**
   * handle tooltip display if agency name exceeds 16 characters
   *
   * @param {number} agencyID - agency link to id
   * @param {string} agencyName - agency link to name
   */
  const handleTooltip = (agencyID, agencyName) => {
    if (agencyID && agencyName.length >= 16) setTooltipID(agencyID);
    else setTooltipID(null);
  };

  /**
   * handle checkbox value and bind to a single array
   *
   * @param {element} event -checkbox input element
   * @param {number} index - checkbox index from the checkbox group
   * @param {any} value - checkbox value
   * @param {string} agencyDisplayName - agency display name
   * @param {function} checkboxValues - Formik's FieldArray Helpers - https://formik.org/docs/api/fieldarray#fieldarray-helpers
   * @param {array} arrayHelpers - checkbox array that stores the values
   */
  const handleCheckbox = (
    event,
    index,
    value,
    agencyDisplayName,
    checkboxValues,
    arrayHelpers,
  ) => {
    if (event.target.checked && !event.target.disabled) {
      arrayHelpers.insert(index, value);
      setAgencyDisplayNameList([...agencyDisplayNameList, agencyDisplayName]);
    } else {
      arrayHelpers.remove(checkboxValues.indexOf(value));
      setAgencyDisplayNameList(
        agencyDisplayNameList.filter(agency => agency !== agencyDisplayName),
      );
    }
  };

  /**
   * set checkbox disabled if an event has already been shared or if an agency is offline
   *
   * @param {string} agencyAccountID - agency account id
   * @param {boolean} agencyActiveStatus agency active status
   *
   * @returns boolean
   */
  const handleDisabled = (agencyAccountID, agencyActiveStatus) => {
    return sharedWithAgencies.includes(agencyAccountID) || agencyActiveStatus;
  };

  /**
   * mark all of the checkboxes as checked
   *
   * @param {array} checkboxValues - checkbox array that stores the values
   * @param {function} arrayHelpers - Formik's FieldArray Helpers - https://formik.org/docs/api/fieldarray#fieldarray-helpers
   */
  const handleSelectAll = (checkboxValues, arrayHelpers) => {
    peerAgencies.forEach((agency, index) => {
      if (
        !checkboxValues.includes(agency.link_to.account_id) &&
        !sharedWithAgencies.includes(agency.link_to.account_id) &&
        agency.link_to.active
      ) {
        arrayHelpers.insert(index, agency.link_to.account_id);
        agencyDisplayNameList.push(agency.link_to.display_name);
        setAgencyDisplayNameList([...agencyDisplayNameList]);
      }
    });
  };

  /**
   * reset the form to its initial state
   *
   * @param {function} handleReset - Formik's reset handler - https://formik.org/docs/api/formik#handlereset---void
   */
  const handleClearAll = handleReset => {
    handleReset();
    setAgencyDisplayNameList([]);
  };

  /**
   * submit form data
   * - save the peer agency account IDs in the callerID's `sharedWithAgencies` array
   * - send the request payload to backend
   *
   * @param {array} agencyAccountIDs - list of peer agency account IDs
   */
  const handleSubmit = (agencyAccountIDs, message) => {
    currentCallLocation = {
      ...currentCallLocation,
      agencyShareChat: {
        ...currentCallLocation?.agencyShareChat,
        eventChatParticipants: getEventChatParticipants(agencyAccountIDs),
        eventSharedBy: orgName,
      },
    };

    const filteredIDs = [];
    agencyAccountIDs.forEach(id => {
      if (!sharedWithAgencies.includes(id)) filteredIDs.push(id);
    });

    const reduxData = {
      accountIDs: agencyAccountIDs,
      callerID: selectedCallerID,
      chatParticipants:
        currentCallLocation?.agencyShareChat?.eventChatParticipants,
      conversationID: null,
    };

    const requestData = {
      event: currentCallLocation,
      eventType: currentCallLocation?.agencyShareChat?.eventType,
      message: message || '',
      uri: `tel://${selectedCallerID}`,
      orgName: orgName,
      shareWith: filteredIDs,
    };

    const currentCallADR = JSON.parse(
      JSON.stringify(currentCall.additionalData),
    );

    if (Object.keys(currentCallADR).length) {
      requestData.adrData = currentCallADR;
    }

    trackShareButtonClick(irpVersion);

    dispatch(shareEmergencyCall(requestData))
      .then(unwrapResult)
      .then(response => {
        reduxData.conversationID = response.conversation_id;
      })
      .then(() => {
        dispatch(setSharedWithAgencies(reduxData));
        agencyDisplayNameList.forEach(agencyName =>
          toastShared(agencyName, currentCallLocation.source),
        );
      })
      .catch(error => {
        if (error?.data?.detail && typeof error?.data?.detail === 'object') {
          Object.keys(error.data.detail).forEach(key => {
            notifyError(`${key}: ${error.data.detail[key][0]}`);
          });
        } else if (error?.data?.detail) {
          const errorMessage = error.data.detail
            ? error.data.detail
            : API_ERROR_MSG;
          notifyError(errorMessage);
        }
      })
      .finally(onShare());
  };

  useEffect(() => {
    dispatch(fetchAgencyLinks(orgName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik enableReinitialize initialValues={initialValues}>
      {({ handleReset, values }) => (
        <Form>
          <DropdownDialog
            width="215px"
            top={28}
            left={layout?.window?.width <= BP_LAPTOP ? 'auto' : '5px'}
            right={layout?.window?.width <= BP_LAPTOP ? '5px' : 'auto'}
            zIndex={10}
          >
            <DropdownHeader>
              <Title>Share Event</Title>
            </DropdownHeader>
            {peerAgencies ? (
              <>
                <DropdownBody>
                  <Subtitle padding={'14px 20px 7px 20px'}>
                    Select Agency to share
                  </Subtitle>
                  <DropdownList
                    borderless
                    breakpoint={BP_LAPTOP}
                    layoutWidth={layout?.window?.width}
                  >
                    <FieldArray
                      name="peerAgencies"
                      render={arrayHelpers => (
                        <div
                          id="incident-dropdown-checkbox-group"
                          aria-labelledby="incident-dropdown-checkbox-group"
                          role="group"
                        >
                          <DropdownListItem>
                            <FakeLinkComponent
                              type="primary"
                              withPadding={false}
                              onClick={() =>
                                handleSelectAll(
                                  values.peerAgencies,
                                  arrayHelpers,
                                )
                              }
                            >
                              Select All
                            </FakeLinkComponent>
                          </DropdownListItem>
                          {peerAgencies.map((agency, index) => (
                            <DropdownListItem key={agency.link_to.account_id}>
                              <Label>
                                <Checkbox
                                  type="checkbox"
                                  data-name={agency.link_to.name}
                                  name={agency.link_to.name}
                                  value={agency.link_to.account_id}
                                  checked={
                                    values.peerAgencies &&
                                    values.peerAgencies.includes(
                                      agency.link_to.account_id,
                                    )
                                  }
                                  disabled={handleDisabled(
                                    agency.link_to.account_id,
                                    !agency.link_to.active,
                                  )}
                                  onChange={event => {
                                    handleCheckbox(
                                      event,
                                      index,
                                      agency.link_to.account_id,
                                      agency.link_to.display_name,
                                      values.peerAgencies,
                                      arrayHelpers,
                                    );
                                  }}
                                />{' '}
                                <Tooltip
                                  isVisible={agency.link_to.id === tooltipID}
                                  maxWidth={150}
                                  onMouseEnter={() =>
                                    handleTooltip(
                                      agency.link_to.id,
                                      agency.link_to.display_name,
                                    )
                                  }
                                  onMouseLeave={() => handleTooltip(null)}
                                  position="bottom"
                                  text={agency.link_to.display_name}
                                  zIndex={16}
                                >
                                  <Box
                                    align="center"
                                    horizontal
                                    position="relative"
                                    width={'150px'}
                                  >
                                    <Box mr={'5px'} width={'120px'}>
                                      <Text
                                        color="primaryText"
                                        ellipsis
                                        size="normal"
                                      >
                                        {agency.link_to.display_name}
                                      </Text>
                                    </Box>
                                    <ChatAvatar
                                      displayActiveIndicator
                                      isActive={agency.link_to.active}
                                      name={agency.link_to.display_name}
                                    />
                                  </Box>
                                </Tooltip>
                              </Label>
                            </DropdownListItem>
                          ))}
                          <DropdownListItem>
                            <FakeLinkComponent
                              type="primary"
                              withPadding={false}
                              onClick={() => handleClearAll(handleReset)}
                            >
                              Clear All
                            </FakeLinkComponent>
                          </DropdownListItem>
                        </div>
                      )}
                    />
                  </DropdownList>
                  <Subtitle
                    padding={'10px 20px'}
                    isDisabled={!numActiveAgencies}
                  >
                    Send a Message (optional)
                  </Subtitle>
                  <Box padding={'0 20px'}>
                    <Field
                      as={TextArea}
                      name="message"
                      height="65px"
                      maxLength={160}
                      maxWidth="175px"
                      padding="10px"
                      isDisabled={!numActiveAgencies}
                    />
                  </Box>
                </DropdownBody>
                <DropdownFooter>
                  <ShareButton
                    name="incident-dropdown-button-share"
                    data-name="incident-dropdown-button-share"
                    isLoadingProp={isLoading}
                    type="submit"
                    disabled={
                      !values.peerAgencies?.length ||
                      values.peerAgencies === sharedWithAgencies
                    }
                    onClick={() =>
                      handleSubmit(values.peerAgencies, values.message)
                    }
                  >
                    Share
                  </ShareButton>
                </DropdownFooter>
              </>
            ) : (
              <Box align="center" justify="center" padding="12px">
                <Loader />
              </Box>
            )}
          </DropdownDialog>
        </Form>
      )}
    </Formik>
  );
};

AgencyShareChatDropdown.propTypes = {
  onShare: PropTypes.func.isRequired,
  peerAgencies: PropTypes.arrayOf(
    PropTypes.shape({
      account_id: PropTypes.string,
      display_name: PropTypes.string,
    }),
  ),
  theme: PropTypes.object,
};

export default AgencyShareChatDropdown;
